'use client';

import type { FetcherParams } from '@/app/_shared/utils/fetcher/types';
import { ResponseError } from '@/app/_shared/utils/fetcher/types';

// 기본 설정 상수
const DEFAULT_TIMEOUT = 10 * 1000; // 10초
const DEFAULT_CACHE = 'no-store';
const DEFAULT_CREDENTIALS = true;
const ERROR_MESSAGES = {
  TIMEOUT: 'Request timeout',
  NETWORK_ERROR: 'Network error occurred',
} as const;

class ClientFetcher {
  private static instance: ClientFetcher; // 싱글톤 인스턴스
  public baseUrl: string;
  public withCredentials: boolean;
  public timeout: number;

  private constructor() {
    // baseUrl은 fetcher 메서드에서 동적으로 설정
    this.baseUrl = '';
    this.withCredentials = DEFAULT_CREDENTIALS;
    this.timeout = DEFAULT_TIMEOUT;
  }

  // 싱글톤 인스턴스 반환
  static getInstance() {
    if (!ClientFetcher.instance) {
      ClientFetcher.instance = new ClientFetcher();
    }
    return ClientFetcher.instance;
  }

  // 타임아웃 Promise 생성 (signal 사용 금지)
  // signal을 사용하지 않는 이유: Next.js 14 서버 컴포넌트에서 중복 요청 방지 기능이 활성화되지 않음
  private createTimeoutPromise(timeout: number): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(ERROR_MESSAGES.TIMEOUT));
      }, timeout);
    });
  }

  // 응답 데이터 JSON 파싱
  private async parseResponse<T>(response: Response): Promise<T> {
    try {
      return (await response.json()) as T; // JSON 파싱 시도
    } catch {
      return null as unknown as T; // 실패 시 null 반환
    }
  }

  // fetch 요청 메서드
  async fetcher<T, U>({
    url,
    method,
    data,
    params,
    headers,
    baseUrl,
    timeout = this.timeout,
    withCredentials = this.withCredentials,
    cache = DEFAULT_CACHE,
  }: FetcherParams<U>): Promise<T> {
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders,
      body: data ? JSON.stringify(data) : undefined,
      ...(withCredentials && { credentials: 'include' }),
      cache,
    };

    // baseUrl 동적 설정
    const finalBaseUrl = baseUrl || `/api/v2`;

    try {
      // fetch 요청 및 타임아웃 병렬 실행
      const fetchPromise = fetch(
        `${finalBaseUrl}${url}${params ? `?${new URLSearchParams(params).toString()}` : ''}`,
        requestOptions,
      );
      const timeoutPromise = this.createTimeoutPromise(timeout);

      // 요청과 타임아웃 중 먼저 완료된 Promise 반환
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      if (!response.ok) {
        const errorMessage = await response.text();
        const error = new ResponseError(response.status, errorMessage);

        console.error('Error fetching data', {
          url,
          status: response.status,
          error: errorMessage,
          params,
          method,
        });

        throw error;
      }

      return this.parseResponse<T>(response);
    } catch (error) {
      console.error('client fetcher error', {
        url,
        method,
        params,
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      throw error;
    }
  }
}

/**
 * @deprecated utils/fetcher.ts 로 이관되었으므로 더이상 사용하지 않습니다.
 * */
export const clientFetcher = async <T, U = null>(params: FetcherParams<U>): Promise<T> => {
  return ClientFetcher.getInstance().fetcher<T, U>(params);
};
